import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getResource, updateResource} from "../../../../data/actions/resource";
import LocalStorage from "../../../../util/localStorage";
import {Field, FieldsManager} from "../../../../data/services/fields";
import PageFooter from "../../../../common/components/layout/layout-components/page/page-footer";
import {LoaderLarge} from "../../../../common/components/loader";
import FieldSwitchLabel from "../../../../common/components/fields/field-switch/field-switch-label";
import {excludeFields, fieldsToHtml, fillFieldsFromData, includeFields} from "../../../../common/util/util-fields";
import Card from "../../../../common/components/card";
import InfoParagraph from "../../../../common/components/info-paragraph";
import {getProp} from "../../../../common/util/util-helpers";
import {LINE_OF_BUSINESS_PRIMARY_DRIVER} from "../../../../common/util/util-consts";

export default function CompaniesSettingsGeneralTab({resourcePath, translate, setIsTabDirty}) {

    /** Store
     ================================================================= */
    const dispatch = useDispatch();
    const resource = useSelector((state) => state.resource);
    const isLoading = resource.isLoading;

    /** Data events
     ================================================================= */
    const fetchData = () => {
        dispatch(getResource({
            user: LocalStorage.get('user'),
            resource: getResourceName()
        }))
    }

    const onSubmit = () => {
        const validatedFields = FieldsManager.validateFields(fields)
        if (FieldsManager.checkFieldsForErrors(validatedFields)) {
            dispatch(updateResource({
                user: LocalStorage.get('user'),
                params: FieldsManager.getFieldKeyValues(validatedFields),
                resource: getResourceName(),
                piggyResource: getResourceName(),
                errorMessage: true,
                successMessage: translate("message.settings_updated")
            }))
            setIsStateDirty(false)
        } else {
            setFields(validatedFields)
        }
    }

    const getFields = (item) => {
        const fieldTemplates = {
            ...(LocalStorage.get('user')?.Contact?.LineOfBusiness.includes('2') && {
                PreventDoubleMCNumber: new Field('PreventDoubleMCNumber', '', [''], false, 'switch', {
                    addContainerClass: "pr-4 col-span-full flex items-center rounded-xl bg-inverse shadow-sm border border-tm-gray-300",
                    htmlBefore: () =>
                        <FieldSwitchLabel
                            label={translate("field.PreventDoubleMCNumber")}
                        />
                }),
                PreventDoubleDOTNumber: new Field('PreventDoubleDOTNumber', '', [''], false, 'switch', {
                    addContainerClass: "pr-4 col-span-full flex items-center rounded-xl bg-inverse shadow-sm border border-tm-gray-300",
                    htmlBefore: () =>
                        <FieldSwitchLabel
                            label={translate("field.PreventDoubleDOTNumber")}
                        />
                }),
                AllowDuplicatesForDifferentCityState: new Field('AllowDuplicatesForDifferentCityState', '', [''], !(item?.PreventDoubleMCNumber && item?.PreventDoubleDOTNumber), 'switch', {
                    addContainerClass: "pr-4 col-span-full flex items-center rounded-xl bg-inverse shadow-sm border border-tm-gray-300",
                    htmlBefore: () =>
                        <FieldSwitchLabel
                            label={translate("field.AllowDuplicatesForDifferentCityState")}
                            note="Note: requires both “Prevent double MC Number” and “Prevent double DOT number” to be enabled"
                        />
                }),
                MakeMCDOTAndAddressRequired: new Field('MakeMCDOTAndAddressRequired', '', [''], false, 'switch', {
                    addContainerClass: "pr-4 col-span-full flex items-center rounded-xl bg-inverse shadow-sm border border-tm-gray-300",
                    htmlBefore: () =>
                        <FieldSwitchLabel
                            label={translate("field.MakeMCDOTAndAddressRequired")}
                        />
                }),
            }),
            ...(LocalStorage.get('user')?.Contact?.LineOfBusiness.includes('1') && {
                PreventDoubleMCNumberCarrier: new Field('PreventDoubleMCNumberCarrier', '', [''], false, 'switch', {
                    addContainerClass: "pr-4 col-span-full flex items-center rounded-xl bg-inverse shadow-sm border border-tm-gray-300",
                    htmlBefore: () =>
                        <FieldSwitchLabel
                            label={translate("field.PreventDoubleMCNumberCarrier")}
                        />
                }),
                PreventDoubleDOTNumberCarrier: new Field('PreventDoubleDOTNumberCarrier', '', [''], false, 'switch', {
                    addContainerClass: "pr-4 col-span-full flex items-center rounded-xl bg-inverse shadow-sm border border-tm-gray-300",
                    htmlBefore: () =>
                        <FieldSwitchLabel
                            label={translate("field.PreventDoubleDOTNumberCarrier")}
                        />
                }),
            }),
            CarrierPacketLegalText: new Field('CarrierPacketLegalText', null, [''], false, 'rich-text', {
                addContainerClass: 'col-span-full',
                labelClass: 'form-group has-float-label font-weight-bold',
            }, {className: "form-control max-w-full min-h-[6rem]"}),
        }

        return fillFieldsFromData(fieldTemplates, item)
    }

    /** UI events
     ================================================================= */
    const handleInputChange = (name, value) => {
        setIsStateDirty(true);
        if (value === undefined) return;

        const fieldsUpdate = FieldsManager.updateField(Object.assign({}, fields), name, value);

        if ((name === 'PreventDoubleMCNumber' || name === 'PreventDoubleDOTNumber') && fieldsUpdate.AllowDuplicatesForDifferentCityState) {
            const bothOn = !!fieldsUpdate.PreventDoubleMCNumber?.value && !!fieldsUpdate.PreventDoubleDOTNumber?.value;
            fieldsUpdate.AllowDuplicatesForDifferentCityState.disabled = !bothOn;
            if (!bothOn) {
                fieldsUpdate.AllowDuplicatesForDifferentCityState.value = 0;
            }
        }

        setFields(fieldsUpdate);
    }

    /** State
     ================================================================= */
    const [fields, setFields] = useState(getFields())
    const [isStateDirty, setIsStateDirty] = useState(false)

    /** Helpers
     ================================================================= */
    const getResourceName = () => {
        return resourcePath;
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (resource?.data && !resource?.isLoading) {
            setFields(getFields(Object.assign({}, resource.data)));
        }
    }, [resource]);

    useEffect(() => {
        setIsTabDirty(isStateDirty);
    }, [isStateDirty]);

    const CompaniesGeneralFields = fieldsToHtml(Object.values(excludeFields(fields, ['CarrierPacketLegalText'])), translate, handleInputChange)
    const companiesLegalTextFields = fieldsToHtml(Object.values(includeFields(fields, ['CarrierPacketLegalText'])), translate, handleInputChange)
    const companiesLegalTextMessage = 'The following legal text will appear at carrier document sign'

    return (
        <React.Fragment>
            <div className="pb-10">

                {!isLoading && (
                    <div className={"max-w-3xl mx-auto space-y-8"}>
                        <Card bodyClass={"py-5 px-6 space-y-4"}>
                            <h1 className={"text-lg leading-6 font-medium text-tm-gray-900"}>{translate('text.company_rules')}</h1>
                            {CompaniesGeneralFields}
                        </Card>

                        {getProp(LocalStorage.get('user'), 'Company.LineOfBusiness', []).includes(LINE_OF_BUSINESS_PRIMARY_DRIVER) ?
                            <div>
                                <Card bodyClass={"py-10 px-6 space-y-4"}>
                                    <h1 className={"text-lg leading-6 font-medium text-tm-gray-900"}>{translate('text.carrier_setup_packet')}</h1>
                                    <InfoParagraph
                                        message={companiesLegalTextMessage}
                                    />
                                    {companiesLegalTextFields}
                                </Card>
                            </div>
                            : null}
                    </div>
                )}

                {!!isLoading && (
                    <LoaderLarge/>
                )}
            </div>

            <PageFooter
                translate={translate}
                canSubmit={isStateDirty}
                actionCancel={() => setFields(getFields(Object.assign({}, resource.data)))}
                actionSubmit={onSubmit}
            />
        </React.Fragment>
    )
}
